import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "src/common/state/hooks";
import ModalStandard from "../Modal/ModalStandard";
import ModalCloseButton from "../Modal/ModalCloseButton";
import AvatarPreview3d from "./AvatarPreview3d";
import Spinner from "../Loader/Spinner";
import type { RootState } from "../../state/store";
import { closeStandardModal } from "../../state/slice/modal/modalSlice";
import { mediaSelector, getIsGalleryLoaded } from "../../../features/gallery/state/gallerySlice";
import type { IMedia } from "../../../model/unityObject";

const Avatar3dPopup = ({
  handleAvatar3dChange,
  currentUserAvatar3dUrl,
}: {
  handleAvatar3dChange: (url: string) => void;
  currentUserAvatar3dUrl: string;
}) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [selectedAvatar, setSelectedAvatar] = useState<IMedia | null>(null);

  const medias = useAppSelector(mediaSelector);
  const isGalleryLoaded = useAppSelector((state: RootState) => getIsGalleryLoaded(state));
  const availableAvatars = medias.filter((m: IMedia) => m.type === "avatar");

  const handleSave = () => {
    if (!selectedAvatar?.s3_url) return;
    // Strip signed AWS query params — the DB column is varchar(255)
    const baseUrl = selectedAvatar.s3_url.split("?")[0];
    handleAvatar3dChange(baseUrl);
    dispatch(closeStandardModal("modalEditAvatar3d"));
    setSelectedAvatar(null);
  };

  return (
    <>
      <ModalStandard id="modalPreviewAvatar3d" fullScreen={false}>
        <div className="flex w-full">
          <div className="relative grow w-full max-w-7xl xl:px-0 mx-auto">
            <h2 className="pt-1 pb-6 font-medium text-lg">
              {t("pages.profile.main.previewAvatar.title")}
            </h2>
            <ModalCloseButton isAvatarField />

            {currentUserAvatar3dUrl ? (
              <AvatarPreview3d
                url={currentUserAvatar3dUrl}
                interactive
                className="w-full h-96 rounded-2xl overflow-hidden"
              />
            ) : (
              <p className="text-sm text-gray-400">—</p>
            )}
          </div>
        </div>
      </ModalStandard>

      <ModalStandard showCloseButton id="modalEditAvatar3d" fullScreen={false}>
        <div className="w-full">
          <h2 className="pt-1 pb-6 font-medium text-lg">
            {t("pages.profile.main.avatar3dCreation.title")}
          </h2>

          {!isGalleryLoaded ? (
            <div className="flex items-center justify-center h-40 relative">
              <Spinner />
            </div>
          ) : availableAvatars.length === 0 ? (
            <div className="flex items-center justify-center h-40 text-gray-500">
              {t("pages.gallery.noAvatarsAvailable", "No avatars available")}
            </div>
          ) : (
            <div className="max-h-96 overflow-y-auto personalize-scroll-visible p-3">
              <div className="grid grid-cols-3 gap-4">
                {availableAvatars.map((avatar: IMedia) => (
                  <div
                    key={avatar.id}
                    className={`cursor-pointer rounded-xl p-2 transition-all bg-white shadow-md hover:scale-105 hover:shadow-2xl hover:ring-green-500 hover:ring-4 ${
                      selectedAvatar?.id === avatar.id ? "ring-purple-500 ring-4 scale-105" : ""
                    }`}
                    onClick={() => setSelectedAvatar(avatar)}
                  >
                    <img
                      src={avatar.thumbnail_s3_url || avatar.s3_url}
                      alt={avatar.name}
                      className="w-full h-32 object-cover rounded"
                    />
                    <p className="text-xs text-center mt-1 truncate">{avatar.name}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="flex justify-end gap-3 mt-4 pt-4 border-t border-gray-200">
            <button
              type="button"
              className="btn-primary-outline"
              onClick={() => {
                dispatch(closeStandardModal("modalEditAvatar3d"));
                setSelectedAvatar(null);
              }}
            >
              {t("general.cancel")}
            </button>
            <button
              type="button"
              disabled={!selectedAvatar}
              className={`btn-primary-fill ${!selectedAvatar && "cursor-not-allowed"}`}
              onClick={handleSave}
            >
              {t("general.save")}
            </button>
          </div>
        </div>
      </ModalStandard>
    </>
  );
};

export default Avatar3dPopup;
